import { isEmpty, isNil } from 'lodash';
import moment from 'moment';
import { useCallback } from 'react';
import { useControlsState, useGroupContext } from '@shared/components/filter/state';
import {
  FilterBooleanSelection,
  FilterDateSelection,
  FilterNumberSelection,
  FilterPersonSelection,
  FilterRelatedCardSelection,
  FilterSelectionState,
  FilterTagSelection,
  FilterTextSelection,
} from '@shared/components/filter/types';
import { getRelativeDateFilters } from '@shared/data/cards/CardsEntityService';
import {
  isDateComponent,
  isNumberComponent,
  isPersonComponent,
  isRelatedCardComponent,
  isTagComponent,
  isTextComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { useCurrentUserId } from '@shared/util/hooks';

type IsCardInFilterGroupHandler = (entity: API.WorkflowEntity) => boolean;

export function useCardIsInFilterGroup(): IsCardInFilterGroupHandler {
  const currentUserId = useCurrentUserId();
  const group = useGroupContext();
  const { filters } = useControlsState();

  return useCallback(
    (entity: API.WorkflowEntity) => {
      const allFilters = [
        ...Array.from(group?.values() || []),
        ...Array.from(filters?.values() || []),
      ];
      return getCardIsInFilterGroup({ entity, filters: allFilters, currentUserId });
    },
    [group, filters, currentUserId],
  );
}

interface GetCardIsInFilterGroupProps {
  entity: API.WorkflowEntity;
  filters: FilterSelectionState[];
  currentUserId?: number;
}

export function getCardIsInFilterGroup({
  entity,
  filters,
  currentUserId,
}: GetCardIsInFilterGroupProps): boolean {
  const cardIsInGroup = filters.every(filterOption => {
    const filterComponent = filterOption.filter?.component;
    if (isNil(filterComponent)) {
      return false;
    }

    if (!isEmpty(filterOption.filter?.relatedEntityPath)) {
      // A cross-workflow filter targets a component on a *related* entity, which isn't in
      // `entity.fields` - there's nothing to match against here. Skip the check (like the
      // unrecognized-component-type case below); `useUpdateList` decides what to do with a card
      // whose standing against it is unknown.
      return true;
    }

    const fieldValue = entity.fields?.[filterComponent.id];

    const scalarKey = filterOption.filter?.scalarKey;
    if (!isNil(scalarKey)) {
      if (scalarKey === 'TIME_TRACKER_IS_ACTIVE_BOOLEAN') {
        const selection = filterOption.selection as FilterBooleanSelection;
        // A timer is running when it has an entry that was started and not yet ended. Its field
        // value is the raw list of entries (see `TimeTrackerField`); anything else, including no
        // value at all, is not running.
        const isRunning =
          Array.isArray(fieldValue) &&
          fieldValue.some(entry => !isNil(entry?.start) && isNil(entry?.end));
        return isRunning === selection.value;
      }
      // Every other scalar representation is a reading of the field the backend derives, not the
      // field value itself - a FILE holds an attachment list rather than its joined file names, a
      // SIGNATURE an object rather than the date it was signed. There is nothing here to compare
      // faithfully, so skip the check (like the cross-workflow case above) rather than guessing
      // and hiding a card that may well belong. The server refetch settles it.
      //
      // A key that *can* be evaluated locally belongs above this, as a branch of its own.
      return true;
    }

    if (isNumberComponent(filterComponent)) {
      const numberSelection = filterOption.selection as FilterNumberSelection;
      const selectedValue = numberSelection.value;
      const selectedOperator = numberSelection.operator;

      switch (selectedOperator) {
        case 'empty':
          return isNil(fieldValue);
        case 'EQ':
          return fieldValue === selectedValue;
        case 'GT':
          return fieldValue > selectedValue;
        case 'GTE':
          return fieldValue >= selectedValue;
        case 'LT':
          return fieldValue < selectedValue;
        case 'LTE':
          return fieldValue <= selectedValue;
      }
    }

    if (isDateComponent(filterComponent)) {
      const dateSelection = filterOption.selection as FilterDateSelection;
      const selectedAfter = dateSelection.after;
      const selectedBefore = dateSelection.before;
      const isRelativeBound =
        !!dateSelection.isRelative &&
        (dateSelection.operator === 'startsOn' || dateSelection.operator === 'endsOn');

      if (dateSelection.operator === 'empty') {
        return isNil(fieldValue);
      }

      // A card with no date cannot match any date-range operator.
      // Guard here to avoid moment(undefined) silently returning "now",
      // which would incorrectly match cards with no date against the
      // current week's range.
      if (isNil(fieldValue)) {
        return false;
      }
      if (dateSelection.operator === 'today') {
        return moment(fieldValue).isSame(new Date(), 'day');
      }
      if (dateSelection.operator === 'exactly') {
        return moment(fieldValue).isSame(selectedAfter, 'day');
      }
      if (isRelativeBound) {
        // Resolve the single relative bound to an absolute date against today.
        // `duration` is already signed; startsOn produces `after`, endsOn `before`.
        const relativeDateProps = getRelativeDateFilters({
          isRelative: true,
          comparisonOperator: dateSelection?.comparisonOperator,
          duration: dateSelection?.duration,
          convertToDateRange: true,
        });
        const { after, before } = relativeDateProps.dateRange ?? {};
        if (!isNil(after)) {
          return moment(fieldValue).isSameOrAfter(after);
        }
        if (!isNil(before)) {
          return moment(fieldValue).isSameOrBefore(before);
        }
        return false;
      }
      if (!isNil(selectedAfter) && isNil(selectedBefore)) {
        return moment(fieldValue).isSameOrAfter(selectedAfter);
      }

      if (isNil(selectedAfter) && !isNil(selectedBefore)) {
        return moment(fieldValue).isSameOrBefore(selectedBefore);
      }

      return moment(fieldValue).isBetween(selectedAfter, selectedBefore, undefined, '[]');
    }

    if (isTextComponent(filterComponent)) {
      const { operator, value } = filterOption.selection as FilterTextSelection;
      switch (operator) {
        case 'empty':
          return isEmpty(fieldValue);
        default:
          return !!fieldValue?.toLowerCase().includes(value?.toLowerCase());
      }
    }

    if (isTagComponent(filterComponent)) {
      const tagSelection = filterOption.selection as FilterTagSelection[];
      const selectedValues = tagSelection.map(s => s.value);
      return selectedValues.some(selectedValue => {
        switch (selectedValue) {
          case 'empty':
            return isEmpty(fieldValue);
          default:
            return !!fieldValue?.includes(selectedValue);
        }
      });
    }

    if (isRelatedCardComponent(filterComponent)) {
      const relatedCardSelection = filterOption.selection as FilterRelatedCardSelection[];
      const selectedValues = relatedCardSelection.map(s => s.value);
      return selectedValues.some(selectedValue => {
        switch (selectedValue) {
          case 'empty':
            return isEmpty(fieldValue);
          default:
            return !!fieldValue?.find((item: any) => item.id === selectedValue);
        }
      });
    }

    if (isPersonComponent(filterComponent)) {
      const personSelection = filterOption.selection as FilterPersonSelection[];
      const selectedValues = personSelection.map(s => s.value);
      return selectedValues.some(selectedValue => {
        switch (selectedValue) {
          case 'empty':
            return isEmpty(fieldValue);
          case 'currentUser':
            return !!fieldValue?.includes(currentUserId);
          default:
            return !!fieldValue?.includes(selectedValue);
        }
      });
    }

    return true;
  });

  return cardIsInGroup;
}
